import { zodResolver } from '@hookform/resolvers/zod';
import {
  GENDERS,
  GENDER_LABELS,
  NATIONALITIES,
  PREFERRED_CONTACTS,
  TITLES,
  personalDetailsSchema,
  type PersonalDetails,
} from '@pgs/shared';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { AddressSection } from '../AddressSection';
import { focusFirstError } from '../focusFirstError';
import { NavButtons } from '../NavButtons';
import { StepPanel } from '../StepPanel';
import { DateInput } from '@/components/form/DateInput';
import { NIInput } from '@/components/form/NIInput';
import { NameInput } from '@/components/form/NameInput';
import { RadioGroup } from '@/components/form/RadioGroup';
import { SelectField } from '@/components/form/SelectField';
import { TextField } from '@/components/form/TextField';

interface Props {
  defaultValues?: Partial<PersonalDetails>;
  authEmail?: string | null;
  onSubmit: (values: PersonalDetails) => void;
}

export const Step0Personal = ({ defaultValues, authEmail, onSubmit }: Props) => {
  const form = useForm<PersonalDetails>({
    resolver: zodResolver(personalDetailsSchema),
    defaultValues: {
      ...defaultValues,
      email: defaultValues?.email ?? authEmail ?? undefined,
    },
    mode: 'onBlur',
  });
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (!defaultValues?.email && authEmail) {
      setValue('email', authEmail, { shouldValidate: false, shouldDirty: false });
    }
  }, [authEmail, defaultValues?.email, setValue]);

  return (
    <StepPanel
      icon="fa-user"
      title="Personal Details"
      description={
        <>
          <strong className="text-teal">IMPORTANT:</strong> Your name should be the same as on your
          official documents.
          <br />
          <br />
          Please complete as much of the form as you can. This will help avoid delays in
          processing and payroll.
          <br />
          <br />
          Once submitted, a member of our Client Care Team will be in touch with you.
          <br />
          <br />
          If you don&rsquo;t know all the answers, don&rsquo;t worry — just complete it as best as
          you can.
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit, focusFirstError)} className="space-y-0">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Title"
            {...register('title')}
            placeholder="Select title..."
            options={TITLES.map((t) => ({ value: t, label: t }))}
            error={errors.title?.message}
          />
          <RadioGroup
            control={control}
            name="gender"
            label="Gender"
            layout="inline"
            options={GENDERS.map((g) => ({ value: g, label: GENDER_LABELS[g] }))}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <NameInput control={control} name="forename" label="Forename" />
          <NameInput control={control} name="middleName" label="Middle Name(s)" />
          <NameInput control={control} name="surname" label="Last Name" />
        </div>
        <NameInput
          control={control}
          name="preferredName"
          label="Preferred name (e.g. Jonathan → John)"
        />
        <TextField
          label="Mobile"
          placeholder="07123456789"
          maxLength={11}
          inputMode="numeric"
          {...register('mobile')}
          error={errors.mobile?.message}
        />
        <p className="mt-1 text-[11px] italic text-ink-500">
          A mobile number is needed to receive a text message after payroll.
        </p>
        <TextField label="Email" type="email" {...register('email')} error={errors.email?.message} />
        <AddressSection
          control={control}
          register={register}
          errors={errors}
          prefix="address"
          setValue={setValue}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <DateInput control={control} name="dob" label="Date of Birth" />
          <SelectField
            label="Nationality"
            {...register('nationality')}
            options={NATIONALITIES.map((n) => ({ value: n, label: n }))}
            error={errors.nationality?.message}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <NIInput control={control} name="niNumber" label="NI Number" />
          <RadioGroup
            control={control}
            name="preferredContact"
            label="Preferred Contact"
            layout="inline"
            options={PREFERRED_CONTACTS.map((c) => ({
              value: c,
              label: c[0]!.toUpperCase() + c.slice(1),
            }))}
          />
        </div>
        <NavButtons submit />
      </form>
    </StepPanel>
  );
};
